import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Collapse,
  Divider,
  Empty,
  List,
  Modal,
  Radio,
  Space,
  Spin,
  Typography,
  Input,
  message,
  notification,
  Tag,
} from "antd";
import { CommentOutlined } from "@ant-design/icons";
import { getDateTimeFormat } from "util/locale";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { apiRequest } from "util/services";
import { formatInUserTimezone } from "util/formatInUserTimezone";
import styles from "./WelcomeGenaiFeedback.module.scss";

const GENAI_TABLE_METRIC_IDS = new Set([20]);
const GENAI_NARRATIVE_METRIC_IDS = new Set([21, 23, 25]);

/**
 * Matches API welcome/widget/genai-feedback: brand GenAI table (repr 3) or narrative (repr 4).
 */
export function welcomeWidgetSupportsGenaiFeedback(w) {
  const mid = Number(w?.metric_id);
  const rep = Number(w?.representation_type ?? 0);
  if (GENAI_NARRATIVE_METRIC_IDS.has(mid)) return rep === 4;
  if (GENAI_TABLE_METRIC_IDS.has(mid)) return rep === 3;
  return false;
}

/** I18n: welcome.genaiFeedback.<key> — shown when sentiment is Helpful */
const POSITIVE_TAG_I18N_KEYS = [
  "tag_accurate_clear",
  "tag_comprehensive",
  "tag_well_structured",
  "tag_helpful_summary",
];

/** I18n: welcome.genaiFeedback.<key> — shown when sentiment is Needs improvement */
const NEGATIVE_TAG_I18N_KEYS = [
  "tag_incomplete_detail",
  "tag_wrong_emphasis",
  "tag_mismatch_data",
  "tag_repetitive",
  "tag_tone_language",
  "tag_other",
];

export function buildGenaiFeedbackContentSnapshot(widget) {
  const data = widget?.data;
  const mid = Number(widget?.metric_id);
  if (!data || typeof data !== "object") return {};
  if (data.insufficient_reviews) {
    return { insufficient_reviews: true };
  }
  try {
    if (GENAI_TABLE_METRIC_IDS.has(mid)) {
      const items = Array.isArray(data.items) ? data.items : [];
      const lines = items.slice(0, 30).map((row, idx) => {
        const rc =
          row.root_cause ||
          row["Root Cause"] ||
          row.problem ||
          row.label ||
          "";
        const rec =
          row.recommendation || row["Recommendation"] || row.suggestion || "";
        return `#${idx + 1} ${String(rc).trim()}\n→ ${String(rec).trim()}`;
      });
      return {
        metric_id: mid,
        row_count: items.length,
        content_text: lines.join("\n\n").slice(0, 100000),
      };
    }
    const tabs = data.summary_tabs;
    if (Array.isArray(tabs) && tabs.length > 0) {
      const parts = tabs.map((tab) => {
        const label = tab.label || tab.id || "";
        const c = tab.content ?? tab.summary ?? tab.text ?? "";
        const s =
          typeof c === "string"
            ? c
            : c && typeof c === "object"
              ? JSON.stringify(c)
              : "";
        return `[${label}]\n${s}`;
      });
      return {
        metric_id: mid,
        tab_count: tabs.length,
        content_text: parts.join("\n\n").slice(0, 100000),
      };
    }
    const narrative = data.narrative || {};
    const summary = String(narrative.summary || "").trim();
    const bullets = Array.isArray(narrative.bullets)
      ? narrative.bullets.map((b) => String(b).trim()).filter(Boolean)
      : [];
    const text = [summary, ...bullets.map((b) => `• ${b}`)]
      .filter(Boolean)
      .join("\n\n");
    return { metric_id: mid, content_text: text.slice(0, 100000) };
  } catch (e) {
    return { build_error: String(e?.message || e) };
  }
}

function sentimentTagColor(sentiment) {
  if (sentiment === "positive") return "success";
  if (sentiment === "negative") return "error";
  return "default";
}

function HistoryPanelContent({
  historyLoading,
  historyError,
  historyItems,
  userReducer,
  dateFormat,
  t,
}) {
  if (historyLoading) {
    return (
      <div style={{ textAlign: "center", padding: 24 }}>
        <Spin />
      </div>
    );
  }
  if (historyError) {
    return (
      <Typography.Paragraph type="danger" style={{ marginBottom: 0 }}>
        {t("welcome.genaiFeedback.historyLoadError")}
      </Typography.Paragraph>
    );
  }
  if (historyItems.length === 0) {
    return (
      <Empty
        description={t("welcome.genaiFeedback.historyEmpty")}
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }
  return (
    <List
      size="small"
      bordered
      dataSource={historyItems}
      locale={{ emptyText: t("welcome.genaiFeedback.historyEmpty") }}
      renderItem={(item) => (
        <List.Item key={item.id}>
          <List.Item.Meta
            title={
              <div>
                <Space size="small" wrap>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {item.created_at
                      ? formatInUserTimezone(
                          item.created_at,
                          userReducer,
                          dateFormat,
                        )
                      : "—"}
                  </Typography.Text>
                  <Tag color={sentimentTagColor(item.sentiment)}>
                    {item.sentiment === "positive"
                      ? t("welcome.genaiFeedback.positiveShort")
                      : item.sentiment === "negative"
                        ? t("welcome.genaiFeedback.negativeShort")
                        : t("welcome.genaiFeedback.neutralShort")}
                  </Tag>
                  <Tag>
                    {item.submitted_by_me
                      ? t("welcome.genaiFeedback.historyYou")
                      : t("welcome.genaiFeedback.historyTeammate")}
                  </Tag>
                </Space>
                {item.submitter_email ? (
                  <Typography.Text
                    type="secondary"
                    style={{ fontSize: 12, display: "block", marginTop: 4 }}
                    ellipsis={{ tooltip: item.submitter_email }}
                  >
                    {t("welcome.genaiFeedback.historyAuthorEmail", {
                      email: item.submitter_email,
                    })}
                  </Typography.Text>
                ) : null}
              </div>
            }
            description={
              <div>
                {Array.isArray(item.tags) && item.tags.length > 0 ? (
                  <div style={{ marginBottom: 6 }}>
                    {item.tags.map((x) => (
                      <Tag key={x} style={{ marginBottom: 4 }}>
                        {x}
                      </Tag>
                    ))}
                  </div>
                ) : null}
                {item.comment ? (
                  <Typography.Paragraph
                    style={{ marginBottom: 0, whiteSpace: "pre-wrap" }}
                  >
                    {item.comment}
                  </Typography.Paragraph>
                ) : null}
                {!item.comment &&
                !(Array.isArray(item.tags) && item.tags.length) ? (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {t("welcome.genaiFeedback.historyNoDetail")}
                  </Typography.Text>
                ) : null}
              </div>
            }
          />
        </List.Item>
      )}
    />
  );
}

/**
 * Feedback entry point for GenAI widgets (metrics 20 / 21 / 23 / 25).
 * API: sentiment positive|negative, optional tags for both, optional comment.
 *
 * @param {"footer"|"header"} [props.variant] — footer: full widget footer bar; header: compact (legacy).
 */
export function WelcomeGenaiFeedback({
  widget,
  extraWidgetContext,
  disabled,
  variant = "footer",
}) {
  const { t, i18n } = useTranslation();
  const userReducer = useSelector((state) => state.userReducer);
  const [open, setOpen] = useState(false);
  const [sentiment, setSentiment] = useState("positive");
  const [comment, setComment] = useState("");
  const [tags, setTags] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [historyItems, setHistoryItems] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(false);

  const dateFormat = getDateTimeFormat(i18n.language);

  const negativeTagOptions = useMemo(
    () =>
      NEGATIVE_TAG_I18N_KEYS.map((k) => {
        const label = t(`welcome.genaiFeedback.${k}`);
        return { label, value: label };
      }),
    [t],
  );

  const positiveTagOptions = useMemo(
    () =>
      POSITIVE_TAG_I18N_KEYS.map((k) => {
        const label = t(`welcome.genaiFeedback.${k}`);
        return { label, value: label };
      }),
    [t],
  );

  const activeTagOptions =
    sentiment === "positive" ? positiveTagOptions : negativeTagOptions;

  const resetForm = () => {
    setSentiment("positive");
    setComment("");
    setTags([]);
  };

  const loadHistory = useCallback(async () => {
    const wid = Number(widget?.id);
    if (!Number.isFinite(wid) || wid <= 0) return;
    setHistoryLoading(true);
    setHistoryError(false);
    try {
      const res = await apiRequest(
        "welcome/widget/genai-feedback/list",
        "POST",
        { widget_id: wid },
        true,
      );
      if (!res || res.cancel || !res.success) {
        setHistoryError(true);
        setHistoryItems([]);
        return;
      }
      setHistoryItems(Array.isArray(res.items) ? res.items : []);
    } catch {
      setHistoryError(true);
      setHistoryItems([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [widget?.id]);

  useEffect(() => {
    if (open) {
      void loadHistory();
    }
  }, [open, loadHistory]);

  const handleOpen = () => {
    if (disabled) return;
    resetForm();
    setOpen(true);
  };

  const handleSubmit = async () => {
    const wid = Number(widget?.id);
    if (!Number.isFinite(wid) || wid <= 0) return;
    setSubmitting(true);
    try {
      const content_snapshot = buildGenaiFeedbackContentSnapshot(widget);
      const body = {
        widget_id: wid,
        sentiment,
        comment: comment.trim() || undefined,
        tags: tags.length > 0 ? tags.slice(0, 20) : undefined,
        content_snapshot:
          Object.keys(content_snapshot).length > 0 ? content_snapshot : undefined,
        widget_context:
          extraWidgetContext && Object.keys(extraWidgetContext).length > 0
            ? extraWidgetContext
            : undefined,
      };
      const res = await apiRequest(
        "welcome/widget/genai-feedback",
        "POST",
        body,
        true,
      );
      if (!res || res.cancel) {
        notification.error({
          message: t("common.error"),
          description: t("welcome.genaiFeedback.networkError"),
        });
        return;
      }
      if (res.success) {
        resetForm();
        message.success(t("welcome.genaiFeedback.thankYou"));
        await loadHistory();
      } else {
        notification.error({
          message: t("common.error"),
          description:
            res.error || res.message || t("welcome.genaiFeedback.saveFailed"),
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!welcomeWidgetSupportsGenaiFeedback(widget)) {
    return null;
  }

  const trigger = (
    <Button
      type={variant === "footer" ? "default" : "text"}
      size="small"
      icon={<CommentOutlined />}
      disabled={disabled}
      onClick={handleOpen}
      aria-label={t("welcome.genaiFeedback.trigger")}
      className={variant === "footer" ? styles.footerTrigger : undefined}
    >
      {t("welcome.genaiFeedback.trigger")}
    </Button>
  );

  return (
    <>
      {variant === "footer" ? (
        <div className={styles.footer}>{trigger}</div>
      ) : (
        trigger
      )}
      <Modal
        title={t("welcome.genaiFeedback.modalTitle")}
        open={open}
        onCancel={() => !submitting && setOpen(false)}
        onOk={handleSubmit}
        okText={t("welcome.genaiFeedback.submit")}
        cancelText={t("common.cancel")}
        confirmLoading={submitting}
        destroyOnClose
        width={600}
      >
        <div style={{ maxHeight: "62vh", overflowY: "auto", paddingRight: 4 }}>
          <Typography.Title level={5} style={{ marginTop: 0 }}>
            {t("welcome.genaiFeedback.newSectionTitle")}
          </Typography.Title>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
            {t("welcome.genaiFeedback.hint")}
          </Typography.Paragraph>
          <div style={{ marginBottom: 16 }}>
            <Typography.Text strong>
              {t("welcome.genaiFeedback.sentimentLabel")}
            </Typography.Text>
            <Radio.Group
              value={sentiment}
              onChange={(e) => {
                setSentiment(e.target.value);
                setTags([]);
              }}
              style={{ display: "block", marginTop: 8 }}
            >
              <Space direction="vertical">
                <Radio value="positive">
                  {t("welcome.genaiFeedback.positive")}
                </Radio>
                <Radio value="negative">
                  {t("welcome.genaiFeedback.negative")}
                </Radio>
              </Space>
            </Radio.Group>
          </div>
          <div style={{ marginBottom: 16 }}>
            <Typography.Text strong>
              {sentiment === "positive"
                ? t("welcome.genaiFeedback.tagsLabelPositive")
                : t("welcome.genaiFeedback.tagsLabel")}
            </Typography.Text>
            <Checkbox.Group
              options={activeTagOptions}
              value={tags}
              onChange={(v) => setTags(v)}
              style={{ display: "block", marginTop: 8 }}
            />
          </div>
          <div style={{ marginBottom: 8 }}>
            <Typography.Text strong>
              {t("welcome.genaiFeedback.commentLabel")}
            </Typography.Text>
            <Input.TextArea
              style={{ marginTop: 8 }}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t("welcome.genaiFeedback.commentPlaceholder")}
              maxLength={8000}
              rows={4}
              showCount
            />
          </div>

          <Divider style={{ margin: "16px 0 8px" }} />

          <Collapse
            bordered={false}
            style={{ background: "transparent" }}
            items={[
              {
                key: "history",
                label: `${t("welcome.genaiFeedback.historyCollapse")} (${historyItems.length})`,
                children: (
                  <HistoryPanelContent
                    historyLoading={historyLoading}
                    historyError={historyError}
                    historyItems={historyItems}
                    userReducer={userReducer}
                    dateFormat={dateFormat}
                    t={t}
                  />
                ),
              },
            ]}
          />
        </div>
      </Modal>
    </>
  );
}
